import dayjs from "dayjs";
import { convertSize } from "./DisplayHelper";

// An organisation's licences are calendar days: the API stores each day at
// 12:00 and sends it without a zone (e.g. "2027-10-02T12:00:00"). Only the
// date part counts, read as that day in the browser (local midnight).
export const toLicenseDay = (value) =>
  value ? dayjs(String(value).slice(0, 10)).toDate() : null;

// Today for the licence rules: the UTC calendar day, as the API compares a
// new licence's end with it (DateOnly.FromDateTime(UtcNow)). Held as local
// midnight of that day, like a picked day, so the two compare as days. Near
// midnight this can be a day before or after the browser's own date.
export const licenseToday = (now = Date.now()) => {
  const instant = new Date(now);
  return new Date(
    instant.getUTCFullYear(),
    instant.getUTCMonth(),
    instant.getUTCDate()
  );
};

export const formatLicenseDay = (value) =>
  value ? dayjs(value).format("DD MMM YYYY") : "";

// The API's instants (EnterpriseStatus.IsCurrent): a licence counts from
// 00:00 UTC on its first day until 12:00 UTC on its end day, so the history
// always agrees with the status the API works out.
const utcInstant = (value, hour) => {
  const [y, m, d] = String(value).slice(0, 10).split("-").map(Number);
  return Date.UTC(y, m - 1, d, hour);
};

export const licenseState = (license, now = Date.now()) => {
  if (utcInstant(license.startDate, 0) > now) {
    return "upcoming";
  }
  return utcInstant(license.endDate, 12) > now ? "current" : "ended";
};

// The licence in force now, as the API picks it (EnterpriseStatus
// .CurrentLicense): of the current ones, the one ending last (two are current
// until 12:00 UTC on the day one ends and the next starts). Null when none is.
export const currentLicense = (licenses, now = Date.now()) =>
  (licenses || [])
    .filter((license) => licenseState(license, now) === "current")
    .sort((a, b) => utcInstant(b.endDate, 12) - utcInstant(a.endDate, 12))[0] ||
  null;

// The first day of the soonest licence the API counts as not started yet,
// for "Starts on".
export const nextLicenseStart = (licenses, now = Date.now()) =>
  (licenses || [])
    .filter((license) => utcInstant(license.startDate, 0) > now)
    .map((license) => toLicenseDay(license.startDate))
    .sort((a, b) => a - b)[0] || null;

// A new licence may start on the day the latest one ends, so it carries on
// from there; once that day has passed it starts today.
export const defaultLicenseStart = (latestEnd, now = Date.now()) => {
  const today = licenseToday(now);
  return latestEnd && latestEnd >= today ? latestEnd : today;
};

// The API's rules for a new licence, field by field. The values are the API's
// error codes (or "required"), so a refusal from the API reads the same.
export const validateNewLicense = (
  { startDate, endDate } = {},
  { latestEnd = null, now = Date.now() } = {}
) => {
  const today = licenseToday(now);
  const errors = {};
  if (!startDate) {
    errors.startDate = "required";
  } else if (latestEnd && startDate < latestEnd) {
    errors.startDate = "LICENSE_OVERLAPS";
  }
  if (!endDate) {
    errors.endDate = "required";
  } else if (startDate && endDate <= startDate) {
    errors.endDate = "LICENSE_END_BEFORE_START";
  } else if (endDate <= today) {
    errors.endDate = "LICENSE_END_IN_PAST";
  }
  return errors;
};

// The message ids of the error codes the API sends in "response".
const API_ERROR_MESSAGES = {
  LICENSE_END_BEFORE_START: "license_error_end_before_start",
  LICENSE_END_IN_PAST: "license_error_end_in_past",
  LICENSE_OVERLAPS: "license_error_overlaps",
  INVALID_LICENSE: "license_error_invalid",
  INVALID_ENTITLEMENT: "entitlement_error_invalid",
};

const LICENSE_ERROR_FIELDS = {
  LICENSE_END_BEFORE_START: "endDate",
  LICENSE_END_IN_PAST: "endDate",
  LICENSE_OVERLAPS: "startDate",
};

export const licenseErrorMessageId = (code) => API_ERROR_MESSAGES[code] || null;

// The field a licence error belongs under; null for the whole form.
export const licenseErrorField = (code) => LICENSE_ERROR_FIELDS[code] || null;

export const toLicenseRequest = ({ startDate, endDate }) => ({
  startDate: dayjs(startDate).format("YYYY-MM-DD"),
  endDate: dayjs(endDate).format("YYYY-MM-DD"),
});

// The API's entitlement types (EntitlementType): 1 the user limit, 2 the
// storage in bytes.
export const ENTITLEMENT_USERS = 1;
export const ENTITLEMENT_STORAGE = 2;

// An entitlement's value by its type, whatever order the list comes in;
// undefined when the organisation has none of that type.
export const entitlementValue = (entitlements, type) =>
  (entitlements || []).find((entitlement) => entitlement?.type === type)
    ?.value;

// The same conversion as the add wizard (DisplayHelper.convertSize), in whole
// bytes for the API.
export const gbToBytes = (gb) =>
  Math.round(convertSize(parseFloat(gb), "GB", "B"));

// For display, to 2 decimals: 0.1 GB is 107374182 bytes, which reads back as
// 0.09999999906867743 unrounded.
export const bytesToGb = (bytes) =>
  Math.round(convertSize(parseInt(bytes || 0), "B", "GB") * 100) / 100;

const isBlank = (value) =>
  value === undefined || value === null || String(value).trim() === "";

// A user limit is a whole number of at least 1; storage is more than 0 GB.
export const validateEntitlements = ({ users, storage } = {}) => {
  const errors = {};
  if (isBlank(users)) {
    errors.users = "required";
  } else if (!/^\d+$/.test(String(users).trim()) || Number(users) < 1) {
    errors.users = "entitlement_users_invalid";
  }
  if (isBlank(storage)) {
    errors.storage = "required";
  } else if (!(Number(storage) > 0)) {
    errors.storage = "entitlement_storage_invalid";
  }
  return errors;
};
